import TextArea from "@mecha/design-components/dist/src/components/Elements/TextArea";
import Textbox from "@mecha/design-components/dist/src/components/Elements/Textbox";
import { useEffect, useState } from "react";
import Logger from "../../../../libs/Logger";
import dayjs from "dayjs";
import { generateApiKey } from "../../../../api/apiKeysApis";
import { showErrorToast } from "../../../../utils/errorsToast";
import { ErrorReporter } from "../../../../libs/ErrorReporter";
import { ICommonCloudApiResponse } from "../../../../interfaces";
import { customToast } from "../../../../utils/customToast";
import { EventTracker } from "../../../../libs/EventTracker";
import { useStore } from "../../../../storeManager/stores";
import CautiousIcon from "../../../../assets/svg/CautiousIcon";
import { SelectDropDown } from "@mecha/design-components/dist/src/components/Elements/Select";
import Button from "@mecha/design-components/dist/src/components/Elements/Button";
import { ApiKeyModalSteps, ApiKeyStatus } from "../../../../config/constants";
import { observer } from "mobx-react";
import { EventTrackingCodes } from "../../../../interfaces/eventsInterfaces";

// export const API_KEY_REGEX =
//   /^[a-zA-Z](?!.*(?:-{2,}|\s{2,}))(?:[a-zA-Z0-9_-\s]*_?[a-zA-Z0-9_\s]*)*$/;

function testApiKeyFormat(apiKey) {
  // Check if the first character is an alphabetic character
  if (!apiKey[0].match(/[a-zA-Z]/)) {
      return false;
  }

  // Check for consecutive hyphens or consecutive whitespaces
  for (let i = 0; i < apiKey.length - 1; i++) {
      if ((apiKey[i] === '-' && apiKey[i + 1] === '-') || (apiKey[i] === ' ' && apiKey[i + 1] === ' ')) {
          return false;
      }
  }

  // Check for underscore usage
  let underscoreUsed = false;
  for (let char of apiKey) {
      if (char === '_') {
          underscoreUsed = true;
      } else if (!char.match(/[a-zA-Z0-9\s-]/)) {
          return false;
      }
  }

  // If underscore is used, it should not be the last character
  if (underscoreUsed && apiKey[apiKey.length - 1] === '_') {
      return false;
  }

  return true;
}

const ConfigureKeyModal = () => {
  const log = new Logger({
    module: "Settings",
    component: "Configure Machine",
  });
  const { setApiKeyModal, setGenerateKeyModalStep, setGeneratedAPIKeyData } =
    useStore().settingsStore;
  const timeStamps = [
    { label: "7 days", value: "7" },
    { label: "30 days", value: "30" },
    { label: "90 days", value: "90" },
    { label: "180 days", value: "180" },
    { label: "365 days", value: "365" },
    { label: "No Expiry", value: "NO_EXPIRY" },
  ];
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [expiryInfo, setExpiryInfo] = useState(timeStamps[0]);
  const [expiryDate, setExpiryDate] = useState("");
  const [formattedExpiryDate, SetFormattedExpiryDate] = useState("");
  const [error, setError] = useState({ name: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    handleDate(expiryInfo.value);
  }, [expiryInfo]);

  const handleDate = expiryDateValue => {
    console.log("date type selected in the dropdown: ", expiryDateValue);
    log.debug(
      { expiryDateValue },
      "(handleDate) - date type selected in the dropdown",
    );

    const currentDate = dayjs().add(expiryDateValue, "days");

    const formattedDate = currentDate.format("DD-MM-YYYY");
    SetFormattedExpiryDate(currentDate.format("DD MMMM, YYYY"));
    log.debug(
      { formattedDate },
      `(handleDate) - formatted date for {dateType: ${expiryDateValue}}`,
    );
    setExpiryDate(formattedDate);
  };

  const handleSubmit = async () => {
    setLoading(true);
    if (!name) {
      log.warn({}, "(handleSubmit) - name field submitted empty");
      setError({ name: "Please enter name" });
      setLoading(false);
      return;
    }
    const validKey = testApiKeyFormat(name);
    if (!validKey) {
      setError({ name: "Please enter valid key name" });
      setLoading(false);
      return;
    }
    const requestBody = {
      name: name,
      description: description,
      expiryDate:
        expiryInfo.label === "No Expiry" ? ApiKeyStatus.NO_EXPIRY : expiryDate,
    };
    log.debug(
      { requestBody },
      "(handleSubmit) - request body for generate api key",
    );

    try {
      const createApiKeyResponse: ICommonCloudApiResponse =
        await generateApiKey(requestBody);
      if (!createApiKeyResponse?.success) {
        setLoading(false);
        return;
      }
      EventTracker.track(EventTrackingCodes.APIKEY_ADDED, { requestBody });
      customToast({
        msg: "Generated API key successfully",
        toastType: "SUCCESS",
      });
      log.info({}, "(handleSubmit) - API Key generated successfully");
      EventTracker.track("New API Key generated");
      const keyData = {
        ...requestBody,
        expiryDate:
        expiryInfo.label === "No Expiry" ? ApiKeyStatus.NO_EXPIRY : formattedExpiryDate,
        accessKeyId: createApiKeyResponse?.payload?.accessKeyId || "",
        accessKeySecret: createApiKeyResponse?.payload?.accessKeySecret || "",
      };
      setGeneratedAPIKeyData(keyData);
      setGenerateKeyModalStep(ApiKeyModalSteps.STEP2);
    } catch (err) {
      if (err.message === "Api key exist with provided name!") {
        setError({ name: "This name already exists, please try a new one" });
        log.warn(
          {},
          `(handleSubmit) - Already existing name submitted {name: ${name}}`,
        );
        return;
      }
      log.error({ error: err }, "(handleSubmit) - Error in generating api key");
      showErrorToast(err);
      ErrorReporter.collect(err, "Error in generating API Key");
    } finally {
      setLoading(false);
    }
  };

  const handleEnterStage = () => {
    setApiKeyModal(false);
  };
  const handleChange = value => {
    setExpiryInfo(value);
  };

  const handleNameChange = event => {
    setName(event.target.value.trim());
    setError({ name: "" });
    setLoading(false);
  };

  return (
    <div className="flex flex-col max-h-[calc(100vh-120px)]">
      <div className="mt-2 flex flex-col gap-5">
        <div>
          <p className="font-medium text-web-b5 dark:text-dark-n-500 text-light-n-500">
            Key name
          </p>
          <Textbox
            className="__input-api-key-name-required mt-[6px]"
            fullWidth
            boxSize="m"
            placeholder="Describe primary use"
            maxLength={100}
            onChange={e => handleNameChange(e)}
            style={error.name ? { borderColor: "#F4333F" } : {}}
          />
          {error.name && (
            <div className="flex flex-row items-center">
              <span className="mr-1 dark:text-black text-storm-dust-110">
                <CautiousIcon />
              </span>
              <span>
                <p className="dark:text-dark-n-400 text-light-n-400 text-sm __input-error-invalid-name">
                  {error.name}
                </p>
              </span>
            </div>
          )}
        </div>
        <div>
          <p className="font-medium text-web-b5 dark:text-dark-n-500 text-light-n-500">
            Expire in
          </p>
          <div className="w-full mt-[6px]">
            <SelectDropDown
              options={timeStamps}
              onChange={handleChange}
              value={expiryInfo}
              boxSize="m"
              primaryColor=""
            />
            <p className="text-sm dark:text-custom-420 text-custom-410 flex flex-row items-center gap-3 mt-[6px]">
              <p className="dark:text-white text-neutral-150  __text-expiry-date-on-information">
                {expiryInfo.label === "No Expiry" ? (
                  "No Expiry"
                ) : (
                  <p className="dark:text-custom-420 text-custom-410">
                    This token will expire on{" "}
                    <span className="dark:text-white text-neutral-150">
                      {formattedExpiryDate}
                    </span>
                  </p>
                )}
              </p>
            </p>
          </div>
        </div>
        <div>
          <span className="">
            <p className="font-medium text-web-b5 dark:text-dark-n-500 text-light-n-500">
              Description
            </p>
          </span>
          <TextArea
            boxSize="m"
            rows={4}
            style={{maxHeight: '10em', resize: 'vertical'}}
            fullWidth
            className="mt-[6px] __input-api-key-description"
            placeholder="Describe the role of the API Key"
            name="description"
            maxLength={250}
            onChange={e => {
              setDescription(e.target.value);
            }}
          />
        </div>
      </div>
      <div className="mt-10 flex flex-row items-center justify-end gap-2">
        <Button
          size="m"
          color="secondary-default"
          className="__button-cancel-generate-key"
          onClick={handleEnterStage}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          className="__button-submit-generate-key"
          size="m"
          onClick={handleSubmit}
          disabled={loading}
        >
          Generate key
        </Button>
      </div>
    </div>
  );
};

export default observer(ConfigureKeyModal);
